var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from "react";
import ReactDOM from "react-dom";
import { BlockEngineApiContext } from "../BlockEngineApi.js";
import { BlockEngineApiFrontend } from "../BlockEngineApiFrontend.js";
import { BlockEngineNode } from "./BlockEngineNode.js";
import { getBlock } from "../registerBlock.js";
/**
 * Manage rendering of a page.
 */
export class SmartfireBlockPageRenderer {
    constructor(options = {}) {
        this.root = options.root;
        if (!this.root) {
            // Find the root element
            this.findRoot();
        }
    }
    /**
     * Find on the current page the root element of the page.
     * @returns The root element
     */
    findRoot() {
        const preferredRoot = document.querySelector("smartfire-page-renderer, .smartfire-page-renderer");
        if (preferredRoot) {
            this.root = preferredRoot;
            return this.root;
        }
        // Find all nodes that are smartfire-component or .smartfire-component and choose the uppermost parent that includes all of them
        const smartfireComponentNodes = document.querySelectorAll("smartfire-component, .smartfire-component");
        // Unable to find any smartfire-component or .smartfire-component
        // This is not an error, it just means that the page is not using Smartfire
        if (smartfireComponentNodes.length === 0) {
            return;
        }
        const commonAncestor = this.findCommonAncestor(smartfireComponentNodes);
        if (commonAncestor) {
            return (this.root = commonAncestor);
        }
        // If no common ancestor is found, use the first node
        this.root = smartfireComponentNodes[0];
        return this.root;
    }
    /**
     * Find the common ancestor of the given nodes.
     * @param smartfireComponentNodes List of nodes that are smartfire-component or .smartfire-component
     * @returns Common ancestor of the given nodes or null if no common ancestor is found (happens when the nodes are not in the same tree)
     */
    findCommonAncestor(smartfireComponentNodes) {
        if (smartfireComponentNodes.length < 1) {
            throw new Error("At least one nodes must be provided.");
        }
        const [node1, ...nodes] = Array.from(smartfireComponentNodes);
        let commonAncestor = node1, found = nodes.length === 0;
        do {
            for (let j = 0; j < nodes.length; j++) {
                if (!commonAncestor.contains(nodes[j])) {
                    let parentNode = commonAncestor.parentElement;
                    if (parentNode != null) {
                        commonAncestor = parentNode;
                        break;
                    }
                    else {
                        return null;
                    }
                }
                if (j == nodes.length - 1) {
                    found = true;
                }
            }
        } while (!found);
        return commonAncestor;
    }
    /**
     * Generate the tree of components.
     */
    generateTree() {
        if (!this.root) {
            return;
        }
        if (this.tree) {
            return this.tree;
        }
        const root = new BlockEngineNode({
            node: this.root,
        });
        root.hydrate();
        this.tree = root;
        return root;
    }
    /**
     * Generate React original tree.
     */
    generateReactTree() {
        if (!this.root) {
            throw new Error("Root element not found.");
        }
        const root = this.generateTree();
        const frontendContext = new BlockEngineApiFrontend();
        return React.createElement(React.StrictMode, {}, React.createElement(BlockEngineApiContext.Provider, {
            value: frontendContext,
        }, root === null || root === void 0 ? void 0 : root.childrenAsReactElements()));
    }
    /**
     * Render this page manager on the page.
     */
    render({ preloadBlocks = true, } = {
        preloadBlocks: true,
    }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.root) {
                throw new Error("Root element not found.");
            }
            // Hide root element to avoid CLS (Cumulative Layout Shift) when the blocks are loaded
            this.root.style.display = "none";
            try {
                if (preloadBlocks) {
                    // Avoid increasing CLS (Cumulative Layout Shift) when the blocks are loaded
                    yield this.loadBlocks();
                }
                const reactApp = this.generateReactTree();
                // Support for React 18
                if ("createRoot" in ReactDOM) {
                    console.log("Mounted Smartfire Block Tree on ", this.root);
                    const { createRoot } = ReactDOM;
                    createRoot(this.root).render(reactApp);
                }
                else {
                    console.log("Mounted Smartfire Block Tree on ", this.root);
                    console.warn("React 18 not detected. Using ReactDOM.render instead of ReactDOM.createRoot.");
                    ReactDOM.render(reactApp, this.root);
                }
                return this.root;
            }
            finally {
                // Show root element
                this.root.style.display = "";
            }
        });
    }
    loadBlocks() {
        return __awaiter(this, void 0, void 0, function* () {
            const root = this.generateTree();
            if (!root) {
                console.error("Unable to load blocks. Root not found.");
                return;
            }
            const blockTypes = root.blockTypes;
            const loadedBlocks = yield Promise.all(blockTypes.map((blockType) => __awaiter(this, void 0, void 0, function* () {
                yield getBlock(blockType);
            })));
            console.info(`Loaded ${loadedBlocks.length} block types`, blockTypes);
            return root;
        });
    }
}

//# sourceMappingURL=SmartfireBlockPageRenderer.js.map
